<form [formGroup]="form">
  <h2 class="text-500-medium text-bunny-dark mb-4">
    Logging
    <div class="text-200-regular text-dark-6 mt-2">What Shield captures when a request is detected or blocked.</div>
  </h2>
  <div class="row g-4 mt-0">
    <div class="col-12 col-xl-6 mt-xl-0">
      <bn-web-ui-card
        data-e2e="ShieldWafEnableLogRequestHeaders"
        heading="Log request headers"
        inputType="switch"
        [inputValue]="shield()?.wafRequestHeaderLoggingEnabled || false"
        [isFullHeight]="true"
        [isLoading]="isLoadingToggleLogRequestHeaders()"
        (valueChanged)="onCardInputChange($event, 'wafRequestHeaderLoggingEnabled')">
        <p>If enabled, detected WAF audit logs will include the full list of request headers sent during the request.</p>
        <bn-web-ui-alert
          alertType="light"
          headerIcon="circle-info"
          [showIcon]="true">
          Security Notice: The logs might contain sensitive information, such as access keys or user cookie information.</bn-web-ui-alert
        >
      </bn-web-ui-card>
    </div>

    <div class="col-12 col-xl-6 mt-xl-0">
      <bn-web-ui-card
        data-e2e="ShieldWafEnableLogRequestBody"
        heading="Log request body"
        inputType="switch"
        [inputValue]="shield()?.requestBodyLoggingEnabled || false"
        [isFullHeight]="true"
        [isLoading]="isLoadingToggleLogRequestBody()"
        (valueChanged)="onCardInputChange($event, 'requestBodyLoggingEnabled')">
        <p>Capture the request body (up to your plan limit) alongside headers. Useful for debugging false positives on POST/PUT traffic.</p>
        <bn-web-ui-alert
          alertType="light"
          headerIcon="circle-info"
          [showIcon]="true">
          Only enable when investigating. Bodies can contain upload files and personal data.</bn-web-ui-alert
        >
      </bn-web-ui-card>
    </div>

    <div class="col-12 mt-4">
      <bn-web-ui-card
        heading="Excluded request headers"
        [isFullHeight]="true">
        <p>The headers in this list will not be included in the logs but will still be used for processing WAF rules.</p>
        <div class="bn-container bg-card-inside border-radius-lg shadow-2 mt-2 pt-2 pb-2 px-2 px-sm-4">
          <p class="mb-2 mt-0 color-dark-7">Type a header name and press enter to add it.</p>
          <bn-web-ui-tag-select
            class="mb-2"
            data-e2e="ShieldWafIgnoredHeadersList"
            formControlName="wafRequestIgnoredHeaders"
            inputName="Excluded request headers"
            name="wafRequestIgnoredHeaders"
            placeholder="e.g. Cookie, Authorization, Signature, Credential"
            size="md"
            [clearable]="false" />
        </div>
      </bn-web-ui-card>
    </div>

    <h2 class="text-500-medium text-bunny-dark mt-6">
      Bots
      <div class="text-200-regular text-dark-6 mt-2">
        Choose how Shield treats traffic from known bots and crawlers. Allow, ignore or block per bot or per category.
      </div>
    </h2>

    <div class="col-12">
      <app-shield-bot-controls />
    </div>

    <h2 class="text-500-medium text-bunny-dark mt-6 mb-4">
      Response pages
      <div class="text-200-regular text-dark-6 mt-2">Customize what visitors see when Shield challenges, blocks or rate-limits a request.</div>
    </h2>

    @if (this.shield()?.planType === PlanType.Basic) {
      <bn-web-ui-premium-banner
        bannerImage="/assets/web-ui/images/bunny-shield.svg"
        class="position-relative mb-2 mt-0"
        [active]="true"
        [imageStyles]="{ 'margin-right': '70px', height: '210px' }"
        [noMinHeight]="true">
        <div class="bn-shield-advanced-banner d-flex flex-column gap-1 text-400-regular">
          <div class="mt-2 bn-container--md">
            Enable white-label response pages above to remove bunny.net branding from your challenge, block, and rate-limiting pages. On the Advanced plan, you
            can take full control by customizing the HTML content of each response page to match your brand and user experience.
          </div>
        </div>
        <div class="mt-4">
          <button
            bn-web-ui-button
            itemStyle="primary"
            leftIcon="square-pen"
            size="md"
            (click)="onOpenUpgradeModal()">
            Upgrade to advanced
          </button>
          <button
            bn-web-ui-button
            class="ms-2"
            itemStyle="stroke"
            size="md"
            (click)="onComparePlansClick()">
            Compare plans
          </button>
        </div>
      </bn-web-ui-premium-banner>
    }

    <div class="col-12 mt-0">
      <bn-web-ui-card
        data-e2e="ShieldWafEnableWhiteLabelBlockResponsePages"
        heading="White-label block pages"
        inputType="switch"
        [disableTitle]="shield()?.planType === PlanType.Basic"
        [headingBadges]="[
          {
            context: 'premium',
            text: 'Advanced',
            size: 'sm',
          },
        ]"
        [inputValue]="whitelabelResponsePages()"
        [isFullHeight]="true"
        [isLoading]="isLoadingWhiteLabelBlockResponsePages()"
        [tooltip]="shield()?.planType === PlanType.Basic ? upgradeToAdvancedTooltipTmpl : ''"
        (valueChanged)="onCardInputChange($event, 'whitelabelResponsePages')">
        <p>Replace our bunny.net branded block and challenge pages with a white-labelled experience.</p>
      </bn-web-ui-card>
    </div>
  </div>
</form>
<div
  class="bn-shield-table"
  [ngClass]="{
    'opacity-50 pe-none': !whitelabelResponsePages(),
  }">
  <div class="bn-shield-table__row d-flex flex-column flex-sm-row align-items-sm-center gap-3">
    <span class="d-flex flex-column flex-grow-1">
      <div>
        <span class="text-dark-8 text-200-semi-bold">
          Challenge
          <bn-web-ui-badge
            contentClass="ms-1"
            size="xs"
            [color]="customPages().challenge ? 'red' : 'default'">
            {{ customPages().challenge ? 'Custom' : 'Generic' }}
          </bn-web-ui-badge>
        </span>
      </div>
      <div>
        <p class="mb-0 mt-2 mt-xxl-0">Served with JS challenges (bot detection & managed challenges)</p>
      </div>
    </span>
    <span class="d-flex gap-2">
      @if (customPages().challenge) {
        <button
          bn-web-ui-button
          itemStyle="stroke"
          size="md"
          (click)="onDeleteCustomPage('challenge')">
          Revert to Default
        </button>
        <button
          bn-web-ui-button
          itemStyle="secondary"
          leftIcon="square-pen"
          size="md"
          (click)="onEditCustomPage('challenge')">
          Edit HTML
        </button>
      } @else {
        <button
          bn-web-ui-button
          itemStyle="primary"
          leftIcon="square-pen"
          size="md"
          (click)="onEditCustomPage('challenge')">
          Create custom page
        </button>
      }
    </span>
  </div>
  <div class="bn-shield-table__row d-flex flex-column flex-sm-row align-items-sm-center gap-3">
    <span class="d-flex flex-column flex-grow-1">
      <div>
        <span class="text-dark-8 text-200-semi-bold">
          Block
          <bn-web-ui-badge
            contentClass="ms-1"
            size="xs"
            [color]="customPages().block ? 'red' : 'default'">
            {{ customPages().block ? 'Custom' : 'Generic' }}
          </bn-web-ui-badge>
        </span>
      </div>
      <div>
        <p class="mb-0 mt-2 mt-xxl-0">Served when a request is blocked by WAF, access list or bot detection</p>
      </div>
    </span>
    <span class="d-flex gap-2">
      @if (customPages().block) {
        <button
          bn-web-ui-button
          itemStyle="stroke"
          size="md"
          (click)="onDeleteCustomPage('block')">
          Revert to Default
        </button>
        <button
          bn-web-ui-button
          itemStyle="secondary"
          leftIcon="square-pen"
          size="md"
          (click)="onEditCustomPage('block')">
          Edit HTML
        </button>
      } @else {
        <button
          bn-web-ui-button
          itemStyle="primary"
          leftIcon="square-pen"
          size="md"
          (click)="onEditCustomPage('block')">
          Create custom page
        </button>
      }
    </span>
  </div>
  <div class="bn-shield-table__row d-flex flex-column flex-sm-row align-items-sm-center gap-3">
    <span class="d-flex flex-column flex-grow-1">
      <div>
        <span class="text-dark-8 text-200-semi-bold">
          Rate limiting

          <bn-web-ui-badge
            contentClass="ms-1"
            size="xs"
            [color]="customPages().ratelimit ? 'red' : 'default'">
            {{ customPages().ratelimit ? 'Custom' : 'Generic' }}
          </bn-web-ui-badge>
        </span>
      </div>
      <div>
        <p class="mb-0 mt-2 mt-xxl-0">Served when a visitor breaches a rate-limit rule</p>
      </div>
    </span>
    <span class="d-flex gap-2">
      @if (customPages().ratelimit) {
        <button
          bn-web-ui-button
          itemStyle="stroke"
          size="md"
          (click)="onDeleteCustomPage('ratelimit')">
          Revert to Default
        </button>
        <button
          bn-web-ui-button
          itemStyle="secondary"
          leftIcon="square-pen"
          size="md"
          (click)="onEditCustomPage('ratelimit')">
          Edit HTML
        </button>
      } @else {
        <button
          bn-web-ui-button
          itemStyle="primary"
          leftIcon="square-pen"
          size="md"
          (click)="onEditCustomPage('ratelimit')">
          Create custom page
        </button>
      }
    </span>
  </div>
</div>

<ng-template #upgradeToAdvancedTooltipTmpl>Upgrade to paid plan<br />to use this feature.</ng-template>
